import { useEffect, useMemo } from "react";

import type { EngagedResidentsMonthlyPercentageDto, ProjectEngagementPercentageItemDto } from "@/api/types";
import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { NoData } from "@/components/NoData/NoData";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Inline as LayoutInline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useScrollToPageTop } from "@/hooks/useScrollToPageTop";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { BarChart } from "@/modules/analytics/components/BarChart";
import type { DateRangeOption } from "@/modules/analytics/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

export interface ContributionAnalyticsDetailsProps {
  contribution: EngagedResidentsMonthlyPercentageDto | undefined;
  isLoading: boolean;
  selectedDateRange: DateRangeOption;
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
  onDateRangeChange: (dateRange: DateRangeOption) => void;
}
export function ContributionAnalyticsDetails({
  contribution,
  isLoading,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
}: ContributionAnalyticsDetailsProps): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { i18n, t } = useTranslation();
  const help = useMetricHelp();
  const scrollToTop = useScrollToPageTop();
  useEffect(() => {
    scrollToTop("instant");
  }, [scrollToTop]);
  const contributiontionDataPoints = useMemo(
    () =>
      contribution?.data?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.engagedResidentPercentage,
        isPending: point.isCurrentMonth,
      })) || [],
    [contribution],
  );
  const benchmark = useMemo(
    () =>
      contribution?.benchmark?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.engagedResidentPercentage,
      })) || [],
    [contribution],
  );
  const noContributionDataAvailable =
    !contribution?.data || contribution.data.every((x) => x.engagedResidentPercentage === 0);
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<ProjectEngagementPercentageItemDto>();

    return [
      helper.accessor("range", {
        header: t("page.analytics-details.contribution.table.date"),
        cell: (cell) => <span>{formatDate(i18n, "monthYearShort", parseChartDate(cell.getValue()))}</span>,
        sortFn: (a, b) => b.original.range.from.localeCompare(a.original.range.from),
        enableSorting: true,
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
      helper.accessor("engagedResident", {
        header: t("page.analytics-details.contribution.table.engaged-residents"),
        cell: (cell) => <span>{cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("postCount", {
        header: t("page.analytics-details.contribution.table.posts"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("likeCount", {
        header: t("page.analytics-details.contribution.table.reactions"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("commentLikeCount", {
        header: t("page.analytics-details.contribution.table.comment-likes"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("commentCount", {
        header: t("page.analytics-details.contribution.table.comments"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("chatCount", {
        header: t("page.analytics-details.contribution.table.chats"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("eventCount", {
        header: t("page.analytics-details.contribution.table.events"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("eventsAtendeesCount", {
        header: t("page.analytics-details.contribution.table.event-attendees"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("surveyCount", {
        header: t("page.analytics-details.contribution.table.surveys"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("surveysAnsweredCount", {
        header: t("page.analytics-details.contribution.table.survey-responses"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),

      helper.accessor("pollCount", {
        header: t("page.analytics-details.contribution.table.polls"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("pollsAnsweredCount", {
        header: t("page.analytics-details.contribution.table.polls-answered"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("residentGroupsCreatedCount", {
        header: t("page.analytics-details.contribution.table.resident-groups-created"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("privateTicketCount", {
        header: t("page.analytics-details.contribution.table.tickets"),
        cell: (cell) => (
          <span>
            {cell.row.original.isCurrentMonth
              ? "-"
              : cell.getValue() + cell.row.original.collectiveTicketCount + (cell.row.original.ratedTicketCount ?? 0)}
          </span>
        ),
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("reservationCount", {
        header: t("page.analytics-details.contribution.table.reservations"),
        cell: (cell) => <span>{cell.row.original.isCurrentMonth ? "-" : cell.getValue()}</span>,
        enableSorting: false,
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
    ];
  }, [t, i18n]);
  const tableInstance = useResizableTable(TABLE_KEYS.ANALYTICS_CONTRIBUTION, {
    columns,
    data: contribution?.data || [],
    initialState: {
      sorting: [
        {
          id: "range",
          desc: false,
        },
      ],
    },
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.analytics-details.contribution.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.analytics-overview.overview.title"),
              to: routes.analytics.overview({
                slug,
              }),
            },
            {
              name: t("page.analytics-details.contribution.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{
            scope: "project",
            entity: "analytics social contribution",
            page: "detail",
          }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics-contribution.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics-contribution.suggestion.2"),
          ]}
        />
      }
      header={
        <LayoutInline gap="sm" align="center">
          <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
          <Select
            items={dateRangeOptions}
            selected={selectedDateRange}
            onChange={onDateRangeChange}
            renderOption={(x) => x.name}
            keySelector={(x) => x.value}
          />
        </LayoutInline>
      }
    >
      <PageGrid.Row>
        {/* Row 1 */}
        <PageGrid.Column size="66%" isDesktopIndifferent>
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-details.contribution.chart.contribution.title")}
              tooltip={help("page.analytics-details.contribution.chart.contribution.title.tooltip")}
            />
            {isLoading && (
              <LayoutInline justify="center">
                <Spinner size="xl" />
              </LayoutInline>
            )}
            {!isLoading && noContributionDataAvailable && <NoData error={noContributionDataAvailable} />}
            {!isLoading && !noContributionDataAvailable && (
              <Stack gap="md">
                <span className="text-body text-text-secondary">
                  {t("page.analytics-details.contribution.chart.contribution.description")}
                </span>
                <figure className="flex max-h-screen-minus-64 min-h-scroll-area-standard w-full flex-col">
                  <BarChart
                    dataPrimary={{
                      data: contributiontionDataPoints,
                    }}
                    dataBenchmark={benchmark}
                    xAxisType="date"
                    formatYTick={(y) => `${y}%`}
                    maxY={100}
                    hoverLabel={(data) => (
                      <Stack>
                        <span>
                          {t("page.analytics-overview.contribution.chart.contribution-benchmark.tooltip", {
                            percentage: data.primary?.y || 0,
                            benchmark: data.benchmark?.y || 0,
                          })}
                        </span>
                        {data.primary?.isPending && (
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                          </span>
                        )}
                      </Stack>
                    )}
                  />
                  <ChartLegend
                    items={[
                      {
                        label: t("page.analytics-details.contribution.chart.contribution.legend.primary"),
                        color: dataColors.primary,
                      },
                      {
                        label: t("common.label.aop-benchmark"),
                        color: dataColors.benchmark,
                      },
                      {
                        label: t("page.analytics-details.engagement.chart.legend.current-period"),
                        color: dataColors.primary,
                        pattern: "striped",
                      },
                    ]}
                  />
                </figure>

                <Notice
                  variant="special"
                  message={t("page.analytics-details.contribution.chart.contribution.notice")}
                />
              </Stack>
            )}
          </AnalyticsCard.Root>
        </PageGrid.Column>
        <PageGrid.Column size="33%" isDesktopIndifferent>
          <Stack gap="md">
            <AnalyticsCard.Root height="content" contentAlignment="start">
              <AnalyticsCard.Header title={t("page.analytics-details.contribution.information.title")} />
              <span className="text-body text-text-secondary">
                {t("page.analytics-details.contribution.information.subtitle")}
              </span>
              <ul className="list-disc px-space-md">
                {t("page.analytics-details.contribution.information.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root height="content" contentAlignment="start">
              <AnalyticsCard.Header title={t("page.analytics-details.contribution.best-practices.title")} />
              <span className="text-body text-text-secondary">
                {t("page.analytics-details.contribution.best-practices.subtitle")}
              </span>
              <ul className="list-disc px-space-md">
                {t("page.analytics-details.contribution.best-practices.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
          </Stack>
        </PageGrid.Column>
        {/* Row 2 */}
        <PageGrid.Column size="100%" isDesktopIndifferent>
          <section className="order-3 col-span-3 overflow-x-scroll">
            <AnalyticsCard.Root>
              <AnalyticsCard.Header title={t("page.analytics-details.contribution.table.title")} />
              <Table table={tableInstance} data-testid="contribution-details-table" isLoading={isLoading} />
            </AnalyticsCard.Root>
          </section>
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
